import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialogRef } from "@angular/material";
import { BehaviorSubject } from "rxjs";
import { AdvancedForm, AdvancedFormBuilder } from "../../../model/advanced-form.model";
import { GigotErrorResponse } from "../../../model/error/gigot-error-response.error";
import { MigrationConfigurationForm } from "../../../model/form/migration-configuration.form";
import { StructureVectorForm } from "../../../model/form/structure-vector.form";
import { Logger } from "../../../model/logger.model";
import { MigrationType, toMigrationTypeString } from "../../../persistence/dto/migration-type.enum";
import { CommercialStructureMigration } from "../../../persistence/view-dto/commercial-structure-migration.model";
import { CommercialStructureService } from "../../../service/commercial-structure.service";
import { LoggingService } from "../../../service/logging.service";
import { DialogResult } from "../../../support/dialog-result.model";
import { Enum } from "../../../support/enum.support";
import { Unsubscriber } from "../../../support/unsubscriber.support";
import { StructureVectorComponent } from "../structure-vector/structure-vector.component";

	/**
	 * @description
	 *	Diálogo de migración de estructuras comerciales.
	 */

@Component({
	styleUrls: ["./migration-dialog.component.scss"],
	templateUrl: "./migration-dialog.component.html"
})
export class MigrationDialogComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('sourceVector') sourceVector: StructureVectorComponent;
    @ViewChild('destinationVector') destinationVector: StructureVectorComponent;
	private logger: Logger = this.loggingService.getLogger("migration-dialog.component");
	private unsubscriber = new Unsubscriber();

	public sourceForm: AdvancedForm;
	public destinationForm: AdvancedForm;
	public configurationForm: AdvancedForm;
	public migrationTypes: MigrationType[] = Enum.valuesOf(MigrationType);

	// Observables:
	public isLoading = new BehaviorSubject<boolean>(false);
	public isMigrating = new BehaviorSubject<boolean>(false);
	public isProcessing = new BehaviorSubject<boolean>(false);

	public constructor(
		private commercialStructureService: CommercialStructureService,
		private loggingService: LoggingService,
		private matDialogRef: MatDialogRef<MigrationDialogComponent, DialogResult>) {}

	public ngOnInit(): void {
		this.sourceForm = new AdvancedFormBuilder()
			.logger(this.loggingService.getLogger("migration.source.form"))
			.specification(StructureVectorForm)
			.build();
		this.destinationForm = new AdvancedFormBuilder()
			.logger(this.loggingService.getLogger("migration.destination.form"))
			.specification(StructureVectorForm)
			.build();
		this.configurationForm = new AdvancedFormBuilder()
			.logger(this.loggingService.getLogger("migration.configuration.form"))
			.specification(MigrationConfigurationForm)
			.build();

        this.sourceForm.rawForm().get('campaignId').valueChanges.subscribe((selectedCampaignId) => {
            this.destinationForm.rawForm().get('campaignId').setValue(selectedCampaignId);
            this.destinationForm.rawForm().get('campaignId').disable();
            this.destinationVector.campaignChangedTrigger(selectedCampaignId);
        });
    }

	public ngAfterViewInit(): void {
	}

	public ngOnDestroy(): void {
		this.isLoading.complete();
		this.isMigrating.complete();
		this.isProcessing.complete();
		this.unsubscriber.releaseSubscriptions();
	}

	public onCancel(event: Event): void {
		this.matDialogRef.close(new DialogResult({
			message: "La operación fue cancelada.",
			ok: false
		}));
	}

	public onMigration(event: Event): void {
		this.logger.debug("Migrating...");
		this.isLoading.next(true);
		this.isMigrating.next(true);
		const migration = CommercialStructureMigration.empty();
		this.configurationForm.load(migration);
		this.destinationForm.load(migration.destination);
		this.sourceForm.load(migration.source);
		this.commercialStructureService
			.migrate(migration)
			.subscribe({
				next: _ => {
					this.logger.debug("Migration complete.");
					this.isLoading.next(false);
					this.isMigrating.next(false);
					this.matDialogRef.close(new DialogResult({
						message: "La migración fue realizada con éxito."
					}));
				},
				error: (response: GigotErrorResponse) => {
					this.logger.error(response);
					this.isLoading.next(false);
					this.isMigrating.next(false);
					this.matDialogRef.close(DialogResult.withFailure(response));
				}
			});
	}

	public onProcessing(event: boolean): void {
		this.isProcessing.next(event);
	}

	public toMigrationTypeString(migrationType: MigrationType): string {
		return toMigrationTypeString(migrationType);
	}

	public toMigrationTypeTooltipString(migrationType: MigrationType): string {
		switch (migrationType) {
			case MigrationType.CreateStructureIfNotExists:
				return `Se crearán todas las áreas o zonas necesarias. Luego,
				se aplicará la migración de revendedoras área por área.`;
			case MigrationType.UseDefaultStructure:
				return `Se realizará una migración área por área. En caso de
				que el área destino no exista, se utilizará el área de gestión
				ZZZ09 correspondiente a la zona ZZZ destino.`;
			default:
				return "";
		}
	}
}
